// Panel principal de "configurar la generación". Es el render del step
// `configure` del PresetDetailSheet. Switcha el layout interno según el
// `kind` del preset:
//   • template / design (E-commerce)  → Modelo + Formato + Otra config.
//   • design-modelo                   → delega en `ModeloPanel` (el panel del modo
//     Modelo: Modelo + Pose + Fondo + Formato + Otra config).
//   • design-auto                     → delega en `DesignModeloPanel`, el panel viejo,
//     con `hideAutoPickers`.
//
// Provee también las sub-vistas del picker de Modelo (create/upload) y
// el FormatPickerInline en desktop como vistas que ocupan el panel
// entero (panelView state machine).

import React, { useRef, useState } from 'react';
import {
  ChevronRight,
  Image as ImageIcon,
  LayoutGrid,
  Minus,
  PackageX,
  Pencil,
  Play,
  Plus,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import { CardPicker } from '../components/PanelCatalogoCompacto';
import {
  ModelPickerInline,
  modelSelectionLabel,
  type ModelPickerView,
  type ModelSelection,
} from '../ModelPicker';
import { FormatPickerInline } from '../FormatPicker';
import type { ResolucionImagen } from '../../../lib/resolucionImagen';
import {
  type FondoPickerView,
  type FondoSelection,
} from '../FondoPicker';
import type { PoseV2 } from '../posesApi';
import type { FondoBiblioteca, FondoGuardado } from '../fondosApi';
import type { ModelV2 } from '../modelsApi';
import CachedImg from '../../../components/CachedImg';
import { PromptRecientesButton } from '../components/PromptRecientesButton';
import {
  CONFIG_OPTIONS_V2,
  getFormatLabel,
  type MockProductV2,
} from '../mockData';
import { DesignModeloPanel } from './DesignModeloPanel';
// El modo Modelo monta SU panel —el rediseño que salió del banco de pruebas
// (ADR-2026-09-02-modelo-v2-es-el-modo-modelo)— en vez del de arriba, que quedó para
// `design-auto`.
import { ModeloPanel } from '../modelo/ModeloPanel';
import { FotosProductoPanel } from './FotosProductoPanel';
import { esDesignModelo } from '../modesCatalog';
import { esFondoProducto } from '../components/FondoProductoPicker';
import type { VistaProductoConjuntoOption } from '../vistasCatalogo';
import {
  AnuncioPanel,
  type AnuncioFieldState,
} from './AnuncioPanel';
import { GanchoSection } from '../components/GanchoSection';
// Las MISMAS piezas que dibuja el pie de desktop: la celda gris con la etiqueta arriba,
// el segmentado de Calidad y el valor con chevron
// (ADR-2026-08-28-los-ajustes-se-ven-igual-en-los-tres-modos).
import {
  CalidadSegmentada,
  CampoInstruccion,
  CeldaAjuste,
  ValorConChevron,
  type CalidadImagen,
} from '../components/AjustesRapidosProducto';
import { ContadorImagenes } from '../components/ContadorImagenes';
import { GanchoExplainerSheet } from '../components/GanchoExplainerSheet';
import { isSinGancho } from '../ganchosCatalog';
import { scrollToAndShake } from '../scrollShake';
import {
  resolveDesignModeloCount,
  resumenTandaAutomatica,
  resumenTandaDesignModelo,
} from '../designModeloCount';
import {
  VoiceGuionSection,
  isVoiceGuionIncomplete,
  type VoiceGuionValue,
} from '../components/VoiceGuionSection';
import { usePalabraDeModelo } from '../../../lib/nombreDeModelo';

// Tope del stepper "x imágenes" en plantillas. Mínimo siempre 1. Con más
// de 1, el Generar dispara la producción (carrusel) por las imágenes extra.
const IMAGE_COUNT_MAX = 6;
// Con cuántas imágenes arranca el stepper. Usar una plantilla es casi siempre
// pedir varias tomas de la MISMA escena (el caso carrusel), así que 3 es el
// default y bajar a 1 es la excepción.
// Ver ADR-2026-08-11-plantillas-arrancan-en-3-tomas.
const IMAGE_COUNT_DEFAULT = 3;

export interface ConfigurePanelProps {
  /** Lista de productos elegidos (multi-selección). Mínimo 1. */
  products: MockProductV2[];
  /** La base del anuncio es una creación y no un producto — se lo pasa al
   *  `AnuncioPanel` (ADR-2026-08-05-el-anuncio-desde-una-creacion-usa-el-modo). */
  imagenBase?: { url: string } | null;
  /**
   * Si el preset es 'design' (fila "Diseña tu imagen") mostramos un
   * layout extendido: Modelo + Fondo (2 cols) + Formato (row plana,
   * menos prominente). Si es 'template', el layout original: Modelo +
   * Formato (2 cols) + "Otra cosa" (input libre).
   */
  kind:
    | 'template'
    | 'design'
    | 'design-modelo'
    | 'design-auto'
    | 'design-producto'
    // Modo "Fotos de producto": delega en `FotosProductoPanel` (producto +
    // vistas + fondo reducido + formato). Ver ADR-2026-08-13-modo-fotos-producto.
    | 'fotos-producto'
    // 'catalogo-ia' y 'catalogo-muebles' están en el union pero NO tienen
    // ninguna rama acá: en esos dos modos el step 'configure' lo renderiza
    // `CatalogoCardsFullscreen` (el "Revisá tu catálogo"), no este panel.
    // Figuran solo para que tipe el cast de `PresetDetailSheet`, que excluye
    // 'asistente' y 'video-influencer' pero deja pasar el resto.
    | 'catalogo-ia'
    | 'catalogo-muebles'
    | 'infografia'
    | 'anuncio';
  /**
   * El `configure` se dibuja a DOS COLUMNAS: esta es la de la derecha, y a la izquierda
   * sigue el panel de productos con sus ajustes rápidos y el Generar.
   *
   * Lo que implica para el contenido: sacar de acá todo lo que ya está en la otra
   * columna. Hoy lo usa sólo "Imagen con modelo", donde quedan Modelo y Pose (se pasa
   * derecho a `DesignModeloPanel` como `soloModeloYPose`). Video lo usó un tiempo y
   * volvió a su pantalla completa de siempre
   * (ADR-2026-08-28-video-vuelve-al-diseno-original).
   */
  configureDosColumnas?: boolean;
  /** Si false y kind === 'template', escondemos la sección de Modelo en
   *  el configure (plantillas que solo aceptan producto, no modelo).
   *  Default true para back-compat. */
  acceptsModelo?: boolean;
  /** mobile = botón Modelo abre sheet · desktop = picker inline. */
  variant: 'mobile' | 'desktop';
  modelSelection: ModelSelection | null;
  fondoVal: string | null;
  formatoVal: string | null;
  otraConfig: string;
  // ── Modo 'design-modelo' ─────────────────────────────────────────────
  allPoses: PoseV2[];
  /** Poses propias del cliente — se pasan a `DesignModeloPanel` para que el
   *  carrusel pueda mostrar las que estén elegidas (no viven en `allPoses`). */
  customPoses: PoseV2[];
  poseIds: string[];
  onPoseIdsChange: (ids: string[]) => void;
  /** Toggle "Seleccionar múltiples" del menú de poses (vive en
   *  InterfazV2; el switch se muestra en el PosePickerSheet). Con false
   *  el picker inline reemplaza la selección en cada tap. */
  multiPoseEnabled: boolean;
  onOpenPosePicker: () => void;
  /** Abre el sheet de poses directo en "Subí tu propia pose" — la tarjeta que cierra
   *  la grilla de poses (ADR-2026-08-20-subir-pose-al-final-de-la-grilla). */
  onSubirPose?: () => void;
  bibliotecaFondos: FondoBiblioteca[];
  fondosGuardados: FondoGuardado[];
  fondoSelection: FondoSelection | null;
  onFondoSelectionChange: (sel: FondoSelection) => void;
  onOpenFondoPicker: () => void;
  fondoView: FondoPickerView;
  onFondoViewChange: (v: FondoPickerView) => void;
  onFondoSaved: () => void;
  // ── Modo 'fotos-producto' ────────────────────────────────────────────
  /** Vistas solo-producto compatibles con el producto elegido, ya filtradas
   *  por `model_view` y con el thumbnail resuelto (`vistasSoloProductoPara`).
   *  Opcionales: solo este kind las usa. */
  vistasProducto?: VistaProductoConjuntoOption[];
  /** Slugs de las vistas elegidas — una imagen generada por cada par
   *  (producto activo, vista). */
  vistaIds?: string[];
  onVistaIdsChange?: (ids: string[]) => void;
  /** Productos desactivados por vista (checklist del badge de la grilla,
   *  ADR-2026-08-30-fotos-producto-multi-producto-2). Keyed por slug. */
  vistaProductosExcluidos?: Record<string, string[]>;
  /** Toggle de un producto para una vista (checklist del badge). */
  onToggleVistaProducto?: (vistaId: string, productId: string) => void;
  /** Total de imágenes de la tanda (Σ por vista de productos activos) —
   *  lo muestra el contador del panel. */
  fotosProductoTotalImagenes?: number;
  /**
   * Cuando true (desktop), el botón Generar se separa con `mt-auto` para
   * pegarse al bottom del panel. En mobile (bottom sheet con max-h y
   * scroll) preferimos que Generar quede al final natural del contenido.
   */
  stickyGenerate?: boolean;
  onClearProducts: () => void;
  /** "Añadir producto al outfit" — abre el picker para sumar más productos
   *  a combinar. Se muestra en plantilla / imagen-como-modelo y, como
   *  "Agregar producto", en fotos-producto (multi-producto). */
  onAddProducts?: () => void;
  /**
   * Plantillas dentro del panel de un modo: las elecciones se abren en el PANEL LATERAL,
   * a la derecha, así que en la columna van como CARDS y no como bloques desplegados —
   * ADR-2026-09-08-la-configuracion-de-plantillas-se-elige-con-cards-y-panel-lateral.
   *
   * Con estos handlers puestos (sólo desktop), la card reemplaza al bloque: la de
   * Productos al chip + "Añadir producto al outfit", y la de Modelo al carrusel inline.
   * Sin ellos el panel queda exactamente como estaba, que es lo que ven el resto de los
   * modos y mobile.
   */
  onAbrirProductosLateral?: () => void;
  onAbrirModeloLateral?: () => void;
  /**
   * La PLANTILLA elegida, como una card más arriba de Producto —
   * ADR-2026-09-08-la-plantilla-se-elige-desde-el-panel.
   *
   * La pantalla ya no pasa por la grilla antes de configurar, así que cambiar de
   * plantilla tiene que poder hacerse desde acá: la card la muestra y la abre en el
   * panel lateral, igual que Producto y Modelo.
   */
  plantillaActual?: { titulo: string; imagen?: string | null };
  onAbrirPlantillaLateral?: () => void;
  /**
   * El interruptor Imagen/Video, DENTRO del panel: es una decisión del modo, como las
   * cards, y vivía suelto sobre la plantilla de la derecha
   * (ADR-2026-09-08-el-switch-de-imagen-o-video-va-al-panel).
   */
  switchMedia?: React.ReactNode;
  /** Quita UN producto de la selección (fotos-producto multi-producto).
   *  El caller decide qué pasa al quitar el último. */
  onRemoveProduct?: (id: string) => void;
  onModelChange: (sel: ModelSelection) => void;
  /** Serie de modelos (grilla multi-select de design-modelo): los que siguen
   *  al principal. Cada uno genera su propia imagen. */
  modelSelectionsSerie?: ModelSelection[];
  /** Tap en una card de la grilla: acumula o quita de la serie. */
  onToggleModelSerie?: (sel: ModelSelection) => void;
  // ── Modo "varios modelos" (UI-only) ─────────────────────────────
  modelSelectionsExtra: (ModelSelection | null)[];
  onModelChangeExtras: (next: (ModelSelection | null)[]) => void;
  multiModelEnabled: boolean;
  onMultiModelEnabledChange: (v: boolean) => void;
  onOpenModelPicker: () => void;
  /** Abre el ModelPickerSheet directo a una vista. Lo usa design-modelo
   *  desktop cuando el user toca "Creá tu modelo" o "Cargá modelo" en
   *  el inline picker — en vez de cambiar a inline sub-view, queremos
   *  mostrar el sheet como side panel. */
  onOpenModelSheet: (initialView: ModelPickerView) => void;
  /** Variante del anterior para el sub-flow de multi (slot pre-set). */
  onOpenModelSheetForMultiSlot: (
    slotIdx: number,
    initialView: 'create' | 'upload',
  ) => void;
  /** Productos asignados por slot (modo varios modelos). */
  multiProductsPerSlot: Record<number, MockProductV2[]>;
  /** Tap en "Utilizando"/miniaturas de un slot del MultiView. */
  onTapSlotProducts: (slotIdx: number) => void;
  /** Abre el FondoPickerSheet directo a una vista (describe / upload). */
  onOpenFondoSheet: (
    initialView: FondoPickerView,
    initialCategory?: string,
  ) => void;
  onFondoChange: (v: string | null) => void;
  onFormatoChange: (v: string | null) => void;
  /** Resolución de salida (1K/2K/4K) — sólo la usan 'design-auto' y 'design-modelo',
   *  que son los modos con selector. Ver ADR-2026-08-25-resolucion-de-generacion-2k-4k. */
  resolucion: ResolucionImagen;
  onResolucionChange: (r: ResolucionImagen) => void;
  onOpenFormatPicker: () => void;
  onOtraChange: (v: string) => void;
  /** `cost` = créditos que consumirá esta generación (lo que muestra el pill
   *  del botón). InterfazV2 lo usa para chequear saldo SUFICIENTE, no solo
   *  disponibilidad. Ver ADR-518. */
  onGenerate: (cost: number) => void;
  /** Solo plantillas (kind 'template', modo imagen). Si está provisto,
   *  el stepper "x imágenes" puede pedir más de 1: en ese caso el Generar
   *  hace la generación normal y luego, ya en el inline de imágenes
   *  generadas, dispara la generación de carrusel. `carruselCount` es
   *  cuántas imágenes pedirle al carrusel = (imágenes elegidas − 1),
   *  porque la primera del set es la imagen base recién generada.
   *  `cost` = créditos totales de la producción (base + carrusel). */
  onGenerateProduction?: (carruselCount: number, cost: number) => void;
  /** Costo en créditos del botón "Generar producción" = generación
   *  normal + carrusel (2 imágenes). Se muestra en el pill del botón
   *  igual que el costo del Generar. */
  productionCreditCost?: number | null;
  // ── Modo 'design-auto' (cantidad de imágenes) ────────────────────
  /**
   * Calidad de salida. Sólo la usa el MOBILE de 'design-auto': en desktop este ajuste
   * vive en el pie del panel de productos (`AjustesRapidosProducto`) y este panel ni se
   * monta (ADR-2026-09-02-automatico-mobile-toma-los-ajustes-del-desktop).
   *
   * Opcional porque el resto de los kinds que pasan por acá no la eligen.
   */
  calidad?: CalidadImagen;
  onCalidadChange?: (v: CalidadImagen) => void;
  /**
   * La plantilla abierta elige Calidad (`plantillaEligeCalidad`): la celda va al pie, al
   * lado de Formato, como en el modo Modelo
   * (ADR-2026-09-28-las-plantillas-eligen-la-calidad). La regla la decide el sheet, que
   * es el que sabe si la plantilla es de video y la usa para el precio del pill.
   */
  calidadEnElPie?: boolean;
  /** Cantidad de imágenes a generar — solo se usa cuando el preset es
   *  'design-auto'. Controla el +/- arriba del Generar. */
  autoImageCount: number;
  onAutoImageCountChange: (v: number) => void;
  /** Tope superior del autoImageCount (mínimo siempre es 1). */
  autoImageCountMax: number;
  /** Modelos reales (para el ModelPickerInline desktop). */
  userModels: ModelV2[];
  userAuthId: string | null;
  userCompanyId: string | null;
  onModelCreated: () => void;
  /** Si true, esconde el botón Generar del bottom del panel — el padre
   *  lo renderiza flotante en otra esquina (e.g. design-modelo desktop). */
  hideGenerateButton?: boolean;
  /** Costo en créditos de la generación actual (computado por el padre
   *  a partir del template + multi-preset count + duración). Si está
   *  provisto, se muestra como texto debajo del botón Generar. */
  creditCost?: number | null;
  /** Si true, oculta los botones para agregar/quitar slots del multi-
   *  modelo. Lo usa el flow de E-commerce (kind: 'design') donde la
   *  pose define cuántos modelos van. */
  lockSlotCount?: boolean;
  // ── Modo video (toggle "Imagen / Video" en el header) ──────────────
  /** Cuando true, mostramos el botón "Configurar video" arriba de los
   *  pickers normales. Solo aplica al kind 'template'. */
  videoModeEnabled?: boolean;
  /** Duración seleccionada para los videos a generar (segundos). */
  videoDuration?: number;
  /** Toggle de escenas múltiples. */
  videoMultiScene?: boolean;
  /** Costo en créditos del video por cada imagen generada — solo se usa
   *  para mostrar info al user dentro del botón. */
  videoCreditCostPerImage?: number;
  /** Abre el sheet de configuración de video. */
  onOpenVideoConfig?: () => void;
  // ── Modo "Video con influencer" (dev-only) ─────────────────────────
  /** Cuando true, inyecta la sección "Gancho" (selector + explicador)
   *  como opción adicional, debajo de "Configurar video". Lo activa el
   *  modo `video-influencer`, que reusa este panel como plantilla-video. */
  showGancho?: boolean;
  /** Gancho elegido (controlado desde InterfazV2) — se manda como `gancho_id`
   *  en la generación de video-influencer. */
  ganchoId?: string | null;
  /** "Sin producto" (ADR-673): cambia el chip de Producto y fuerza el guion a
   *  "Escribir" (sin toggle de modos). */
  sinProducto?: boolean;
  /** Autogeneración del diálogo en curso / fallida (ADR-674). */
  dialogoLoading?: boolean;
  dialogoError?: string | null;
  onGanchoIdChange?: (id: string | null) => void;
  /** Sección "Voz y guion" del modo video-influencer (voz + forma de hablar
   *  + guion automático/escrito). Controlado desde InterfazV2, que lo
   *  mapea al payload (`voz_id`, `estilo_voz`, `dialogo*`). ADR-525/558. */
  voiceGuion?: VoiceGuionValue;
  onVoiceGuionChange?: (next: VoiceGuionValue) => void;
  /** Setter de `videoDuration` — en el modo influencer la duración se ajusta
   *  con una barra inline dentro de "Voz y guion" (solo guion Automático),
   *  en vez del botón "Configurar duración" + sheet. ADR-528. */
  onVideoDurationChange?: (v: number) => void;
  // ── Modo 'anuncio' ──────────────────────────────────────────────────
  /** Estados de cada campo textual del modo Anuncio. Cada uno tiene
   *  `mode: 'auto' | 'custom' | 'hidden'` + `value: string`. */
  anuncioTitulo?: AnuncioFieldState;
  onAnuncioTituloChange?: (next: AnuncioFieldState) => void;
  anuncioDescripcion?: AnuncioFieldState;
  onAnuncioDescripcionChange?: (next: AnuncioFieldState) => void;
  anuncioCta?: AnuncioFieldState;
  onAnuncioCtaChange?: (next: AnuncioFieldState) => void;
  /** Hasta 4 hex strings. Si vacío, el panel cae al default
   *  `anuncioBrandColors`. */
  anuncioColors?: string[];
  onAnuncioColorsChange?: (next: string[]) => void;
  /** Paleta del `brand_profile` activo — `brand_profiles.colors`. */
  anuncioBrandColors?: string[];
}

export const ConfigurePanel: React.FC<ConfigurePanelProps> = ({
  products,
  imagenBase,
  kind,
  acceptsModelo = true,
  variant,
  modelSelection,
  formatoVal,
  otraConfig,
  stickyGenerate = false,
  onClearProducts,
  onAddProducts,
  onAbrirProductosLateral,
  onAbrirModeloLateral,
  plantillaActual,
  onAbrirPlantillaLateral,
  switchMedia,
  onRemoveProduct,
  onModelChange,
  modelSelectionsSerie = [],
  onToggleModelSerie,
  modelSelectionsExtra,
  onModelChangeExtras,
  multiModelEnabled,
  onMultiModelEnabledChange,
  onOpenModelPicker,
  onOpenModelSheet,
  onOpenModelSheetForMultiSlot,
  multiProductsPerSlot,
  onTapSlotProducts,
  onOpenFondoSheet,
  onFormatoChange,
  resolucion,
  onResolucionChange,
  onOpenFormatPicker,
  onOtraChange,
  onGenerate,
  onGenerateProduction,
  calidad,
  onCalidadChange,
  calidadEnElPie = false,
  autoImageCount,
  onAutoImageCountChange,
  autoImageCountMax,
  userModels,
  userAuthId,
  userCompanyId,
  onModelCreated,
  allPoses,
  customPoses,
  poseIds,
  onPoseIdsChange,
  multiPoseEnabled,
  onOpenPosePicker,
  onSubirPose,
  bibliotecaFondos,
  fondosGuardados,
  fondoSelection,
  onFondoSelectionChange,
  onOpenFondoPicker,
  fondoView,
  onFondoViewChange,
  onFondoSaved,
  hideGenerateButton = false,
  configureDosColumnas = false,
  creditCost = null,
  // productionCreditCost ya no se usa: el pill ahora muestra creditCost ×
  // cantidad de imágenes del stepper. Se mantiene en la interfaz por
  // compatibilidad con el padre, pero no se destructura.
  lockSlotCount = false,
  videoModeEnabled = false,
  videoDuration = 8,
  videoMultiScene = false,
  onOpenVideoConfig,
  showGancho = false,
  ganchoId = null,
  sinProducto = false,
  dialogoLoading = false,
  dialogoError = null,
  onGanchoIdChange,
  voiceGuion,
  onVoiceGuionChange,
  onVideoDurationChange,
  anuncioTitulo,
  onAnuncioTituloChange,
  anuncioDescripcion,
  onAnuncioDescripcionChange,
  anuncioCta,
  onAnuncioCtaChange,
  anuncioColors,
  onAnuncioColorsChange,
  anuncioBrandColors,
  vistasProducto = [],
  vistaIds = [],
  onVistaIdsChange,
  vistaProductosExcluidos = {},
  onToggleVistaProducto,
  fotosProductoTotalImagenes = 0,
}) => {
  // La palabra de pantalla para la persona de la foto — "Modelo" en Moda, "Avatar"
  // en el resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo).
  const palabra = usePalabraDeModelo();
  const isDesign = kind === 'design';
  const isDesignModelo = esDesignModelo(kind);
  /**
   * Qué panel se monta en el paso de configuración.
   *
   * `design-modelo` monta `ModeloPanel` —el rediseño que ganó el banco de pruebas y hoy
   * ES el modo Modelo (ADR-2026-09-02-modelo-v2-es-el-modo-modelo)—; el resto del
   * `isDesignFlow` (o sea `design-auto`) sigue con `DesignModeloPanel`, que es el panel
   * viejo con `hideAutoPickers`.
   */
  const PanelDeModelo = isDesignModelo ? ModeloPanel : DesignModeloPanel;
  // Modo "Fotos de producto": panel propio (producto + vistas + fondo
  // reducido). No entra en `isDesignFlow` — no comparte pickers con
  // design-modelo/design-auto, solo el chrome del sheet.
  const isFotosProducto = kind === 'fotos-producto';
  // 'design-auto' usa el mismo layout/rama que 'design-modelo' pero
  // sin pickers de modelo/pose/fondo. `isDesignFlow` agrupa ambos para
  // los lugares donde el render es idéntico (sheet sin imagen del
  // preset, panel full-height, etc.).
  const isDesignAuto = kind === 'design-auto';
  const isDesignFlow = isDesignModelo || isDesignAuto;
  const isAnuncio = kind === 'anuncio';
  // Stepper de cantidad de imágenes — reemplaza al viejo botón "Generar
  // producción". Solo plantillas en modo imagen (mismo gating que antes).
  // Con 1 imagen el Generar hace la generación normal; con más de 1
  // dispara la producción (carrusel) igual que el botón anterior.
  const showImageCountStepper =
    kind === 'template' && !videoModeEnabled && !!onGenerateProduction;
  // Cantidad de imágenes elegida en el stepper. Local al panel — por
  // ahora >1 solo activa el envío como producción (que genera un set
  // fijo); el número todavía no parametriza cuántas imágenes pide el
  // backend.
  //
  // Arranca en 3 (`IMAGE_COUNT_DEFAULT`). Consecuencia buscada: el camino por
  // defecto de las plantillas pasa a ser el de producción (`isProductionMode`
  // queda true de entrada), que es exactamente lo que ya pasaba cuando el
  // usuario subía el stepper a mano.
  const [imageCount, setImageCount] = useState(IMAGE_COUNT_DEFAULT);
  // Con más de 1 imagen, el Generar usa la rama de producción; con 1, la
  // generación normal.
  const isProductionMode = showImageCountStepper && imageCount > 1;
  // Contador de cantidad del modo Imagen personalizada. El estado crudo es
  // el mismo `autoImageCount` de design-auto/anuncio; el helper lo baja a 1
  // (y esconde el control) cuando hay varias poses o varios modelos.
  const designModeloCountState = resolveDesignModeloCount({
    rawCount: autoImageCount,
    poseCount: poseIds.length,
    multiModelEnabled,
    modelCount: 1 + modelSelectionsSerie.length,
    max: autoImageCountMax,
  });
  const designModeloCount = designModeloCountState.count;
  // Solo mobile: en desktop el stepper vive en el header del sheet, al lado
  // del pill Generar (el bloque de este botón ni se renderiza ahí).
  const showDesignModeloCount =
    kind === 'design-modelo' &&
    variant === 'mobile' &&
    designModeloCountState.enabled;
  // Resumen de la tanda, arriba del Generar: cuántas imágenes salen y de dónde
  // sale esa cuenta (modelos × poses). Reemplaza al viejo hint de "una imagen
  // por pose", que sólo contemplaba las poses y sólo en mobile.
  //   • pose Automática (poseIds vacío) = 1 pose.
  //   • con 1 modelo y 1 pose la cuenta la fija el contador de Cantidad, y
  //     entonces las imágenes son variaciones del mismo par — decir
  //     "1 modelo × 1 pose" al lado de "4 imágenes" se leería como un error.
  // No aplica al multi-modelo (composición grupal): ahí sale UNA imagen con
  // todos los modelos juntos.
  /**
   * Automático en MOBILE: el contador de imágenes va pegado al Generar, igual que en el
   * pie de desktop (ADR-2026-09-02-automatico-mobile-toma-los-ajustes-del-desktop).
   *
   * Es el único ajuste que cambia lo que cuesta la tanda, así que se decide con el
   * número y el precio a la vista y no arriba, entre Calidad y Formato. En desktop este
   * panel ni se monta —el modo es una sola pantalla—, por eso el gate por variante.
   */
  const autoCountJuntoAlGenerar = isDesignAuto && variant === 'mobile';
  /**
   * PLANTILLAS: la cantidad va pegada al Generar, con el mismo `ContadorImagenes` que
   * Automática y Modelo — ADR-2026-09-08-plantillas-toma-el-contador-de-los-modos.
   *
   * Tenía una fila propia ("Cantidad · Producción · − 3 imágenes +") en una tarjeta
   * blanca sobre el botón, con sus propios botones redondos: el único stepper de la app
   * con esa forma. Ahora es el mismo control, en el mismo lugar y del mismo tamaño que
   * en los otros modos, que es donde el número se decide con el precio a la vista.
   */
  const contadorPlantillaJuntoAlGenerar = showImageCountStepper;
  /** La cantidad que se va a pedir, dentro del rango del modo. */
  const autoCountEfectivo = Math.max(
    1,
    Math.min(autoImageCountMax, Math.trunc(autoImageCount) || 1),
  );
  const resumenTanda =
    kind === 'design-modelo' && !multiModelEnabled
      ? resumenTandaDesignModelo({
          modelCount: 1 + modelSelectionsSerie.length,
          poseCount: poseIds.length,
          count: designModeloCount,
        })
      : autoCountJuntoAlGenerar
        ? // La misma frase que el pie de desktop, del mismo helper: con varios productos
          // NO sale una imagen por producto, sale UNA con todos juntos, y esa sorpresa
          // se paga.
          resumenTandaAutomatica({
            imagenes: autoCountEfectivo,
            productos: products.length,
          })
        : null;
  // Costo mostrado en el pill = 1 crédito por imagen. `creditCost` es el
  // costo de generar 1 imagen con esta plantilla, así que el total del
  // stepper es ese costo × cantidad de imágenes elegidas.
  const activeCreditCost =
    showImageCountStepper && creditCost != null
      ? creditCost * imageCount
      : creditCost;
  // Modo influencer: bloqueamos el Generar si falta el gancho (obligatorio,
  // ADR-546), si no se eligió un modelo explícito (en este modo no existe
  // "Automático") o si el guion/voz elegidos están incompletos (ADR-525/544).
  const ganchoPendiente = showGancho && !ganchoId;
  const modeloPendiente =
    showGancho && (!modelSelection || modelSelection.mode === 'auto');
  const guionPendiente =
    ganchoPendiente ||
    modeloPendiente ||
    (showGancho && !!voiceGuion && isVoiceGuionIncomplete(voiceGuion));
  // Prefijo de ids DOM para el scroll+shake del Generar bloqueado
  // (ADR-552/553) — distinto por variante porque el sheet monta ambos
  // paneles a la vez (ocultos por CSS).
  const influencerIdPrefix = `influencer-${variant === 'desktop' ? 'desktop' : 'mobile'}`;
  // Target del scroll+shake, misma prioridad que el hint: gancho → modelo →
  // voz → guion.
  const influencerMissingTargetId = ganchoPendiente
    ? `${influencerIdPrefix}-gancho-card`
    : modeloPendiente
      ? `${influencerIdPrefix}-modelo-card`
      : voiceGuion && voiceGuion.guionMode !== 'auto' && voiceGuion.vozId == null
        ? `${influencerIdPrefix}-voz-row`
        : `${influencerIdPrefix}-guion-block`;
  // Mensaje del hint (misma prioridad que el target). El último se conserva
  // en un ref para que el texto no se corte seco durante el colapso animado
  // del container (mismo patrón que el pill desktop, ADR-554).
  const influencerHintMsg = ganchoPendiente
    ? 'Elegí un gancho para generar.'
    : modeloPendiente
      ? `Elegí un ${palabra.singular} para generar.`
      : voiceGuion && voiceGuion.guionMode !== 'auto' && voiceGuion.vozId == null
        ? 'Elegí una voz para generar.'
        : voiceGuion?.guionMode === 'audio'
          ? 'Grabá tu guion para generar.'
          : 'Escribí tu guion para generar.';
  // Fotos de producto: sin al menos una vista no hay nada que generar. Mismo
  // tratamiento que el gancho del influencer — el botón no se muere, avisa y
  // sacude la grilla (ADR-552/553).
  const vistasPendientes = isFotosProducto && vistaIds.length === 0;
  const vistasGridId = `vistas-producto-grid-${variant}`;
  // Estado unificado del Generar bloqueado: el hint y el target salen del
  // modo que corresponda (influencer o fotos-producto).
  const generatePendiente = guionPendiente || vistasPendientes;
  const generateHintMsg = vistasPendientes
    ? 'Elegí al menos una vista para generar.'
    : influencerHintMsg;
  const generateMissingTargetId = vistasPendientes
    ? vistasGridId
    : influencerMissingTargetId;
  const lastInfluencerHintRef = useRef<string>('');
  if (generatePendiente) lastInfluencerHintRef.current = generateHintMsg;
  const handleGenerateClick = () => {
    // Costo que se le pasa al guard de créditos = exactamente lo que muestra
    // el pill (`activeCreditCost`). Fallback a 1 si no se pudo computar
    // (nunca es gratis). Ver ADR-518.
    const cost = activeCreditCost != null && activeCreditCost > 0
      ? activeCreditCost
      : 1;
    // Producción: le pedimos al carrusel (imageCount − 1) imágenes, ya que
    // la primera del set es la imagen base que genera el flujo normal.
    if (isProductionMode) onGenerateProduction?.(imageCount - 1, cost);
    else onGenerate(cost);
  };
  const isDesktop = variant === 'desktop';
  // Solo plantillas (kind 'template') pueden no aceptar modelo. Para
  // design/design-modelo siempre se muestra. Si la plantilla declara
  // `inputs_aceptados` sin "modelo", escondemos la sección.
  // 'design-producto' (vistas solo-producto de "Fondo blanco") nunca
  // lleva modelo: mismo layout que una plantilla sin modelo.
  const hideModelo =
    (kind === 'template' && !acceptsModelo) ||
    kind === 'design-producto' ||
    // "Fotos de producto" es, por definición, sin modelo.
    isFotosProducto;
  // En el modo influencer no existe "Automático": mientras no haya un modelo
  // elegido, el botón muestra un placeholder que invita a elegir.
  const modelLabel =
    showGancho && (!modelSelection || modelSelection.mode === 'auto')
      ? `Elegir ${palabra.singular}`
      : modelSelectionLabel(modelSelection);
  const formatoLabel = getFormatLabel(formatoVal);
  // Lookup defensivo del config option de fondo — no se renderiza acá
  // pero mantenemos la referencia para que el lint detecte si el id
  // cambiase en mockData.
  void CONFIG_OPTIONS_V2;

  // View del panel del costado en DESKTOP. Cuando es algo distinto a
  // 'main', se esconde el resto del panel (producto chip, modelo
  // picker, fondo, etc.) y la sub-vista ocupa el panel entero — como un
  // wizard. En mobile siempre se queda 'main' (las sub-vistas son
  // bottom sheets aparte).
  //   • 'main'         → vista normal del config panel
  //   • 'model-create' → sub-vista de modelo personalizado
  //   • 'model-upload' → sub-vista de subir foto de modelo
  //   • 'format'       → sub-vista de elegir formato
  type PanelView =
    | 'main'
    | 'model-create'
    | 'model-upload'
    | 'model-multi'
    | 'format';
  const [panelView, setPanelView] = useState<PanelView>('main');
  const desktopShowOnlyPicker = isDesktop && panelView !== 'main';

  // El ModelPickerInline tiene su propio enum (list/create/upload/multi).
  // Lo mapeamos hacia/desde el panelView para que ambos estén sincronizados.
  const modelView: ModelPickerView =
    panelView === 'model-create'
      ? 'create'
      : panelView === 'model-upload'
        ? 'upload'
        : panelView === 'model-multi'
          ? 'multi'
          : 'list';
  const handleModelViewChange = (v: ModelPickerView) => {
    if (v === 'create') setPanelView('model-create');
    else if (v === 'upload') setPanelView('model-upload');
    else if (v === 'multi') setPanelView('model-multi');
    else setPanelView('main');
  };

  // Click en el chip de Formato: en desktop tomamos el panel; en mobile
  // abrimos el bottom sheet (props del parent).
  /**
   * ¿Esta columna elige con CARDS y panel lateral? — sólo cuando el host pasa los
   * handlers (hoy: una plantilla dentro del panel del modo, en desktop).
   */
  const cardsLateral = isDesktop && !!onAbrirProductosLateral;

  const handleOpenFormat = () => {
    // Con panel lateral, Formato se elige a la derecha como el resto: la sub-vista
    // interna reemplaza TODA la columna —se van de la vista Producto y Modelo— y en esta
    // pantalla las decisiones se abren al costado
    // (ADR-2026-09-08-la-configuracion-de-plantillas-se-elige-con-cards-y-panel-lateral).
    if (cardsLateral) onOpenFormatPicker();
    else if (isDesktop) setPanelView('format');
    else onOpenFormatPicker();
  };

  // Card-sección estilo design-modelo (título + descripción a la izquierda,
  // contenido a la derecha) — la usa el layout desktop full-screen del modo
  // "Video con influencer" (ADR-533/534) para verse como "Generar imagen
  // personalizada".
  const sectionCard = (
    title: string,
    description: string,
    content: React.ReactNode,
    // Nodo opcional debajo de la descripción (ej. link "¿Qué es un gancho?").
    extra?: React.ReactNode,
  ) => (
    <section className="rounded-2xl bg-white border border-neutral-200 p-6 flex gap-6">
      <div className="w-[220px] shrink-0">
        <h3 className="text-[20px] font-semibold text-brand-black/90 leading-tight">
          {title}
        </h3>
        <p className="text-[12.5px] text-[#3C3C43]/60 mt-1.5 leading-relaxed">
          {description}
        </p>
        {extra && <div className="mt-2.5">{extra}</div>}
      </div>
      <div className="flex-1 min-w-0">{content}</div>
    </section>
  );

  // Explicador "¿Qué es un gancho?" — en la card desktop del influencer el
  // link vive en la columna de la descripción (fuera del GanchoSection), así
  // que el estado del sheet se maneja acá (ADR-535).
  const [ganchoExplainerOpen, setGanchoExplainerOpen] = useState(false);

  // Chip de producto(s) + CTA de outfit — se renderiza en el flujo estándar
  // (arriba de todo) y dentro de la card "Producto" del layout desktop del
  // modo influencer. Extraído a const para no duplicar el JSX.
  // "Sin producto" (ADR-673): el chip ocupa el mismo lugar que el del producto
  // elegido, con la misma X para volver a la pantalla de selección.
  const sinProductoChipBlock = (
    <div className="flex items-center gap-3 bg-[#F7F7F9] rounded-2xl p-3">
      <div className="w-12 h-12 rounded-lg bg-white flex items-center justify-center shrink-0">
        <PackageX className="w-5 h-5 text-[#3C3C43]/40" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[14.5px] font-normal text-brand-black/90">Producto</p>
        <p className="text-[14.5px] font-semibold text-brand-black/90 truncate">
          Sin producto
        </p>
      </div>
      <button
        type="button"
        onClick={onClearProducts}
        className="w-8 h-8 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-black/[0.04] transition-colors shrink-0"
        aria-label="Elegir un producto"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );

  const productsChipBlock = sinProducto ? (
    sinProductoChipBlock
  ) : (
    <div className="space-y-2.5">
      <div className="flex items-center gap-3 bg-[#F7F7F9] rounded-2xl p-3">
        {products.length === 1 ? (
          <CachedImg
            src={products[0].imageUrl}
            alt={products[0].name}
            className="w-12 h-12 rounded-lg object-cover shrink-0"
          />
        ) : (
          <div className="flex shrink-0 -space-x-2">
            {products.slice(0, 3).map((p) => (
              <CachedImg
                key={p.id}
                src={p.imageUrl}
                alt={p.name}
                className="w-10 h-10 rounded-lg object-cover ring-2 ring-[#F7F7F9]"
              />
            ))}
            {products.length > 3 && (
              <div className="w-10 h-10 rounded-lg bg-white ring-2 ring-[#F7F7F9] flex items-center justify-center text-[12px] font-semibold text-brand-black/70">
                +{products.length - 3}
              </div>
            )}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[14.5px] font-normal text-brand-black/90">
            {products.length > 1 ? 'Productos' : 'Producto'}
          </p>
          <p className="text-[14.5px] font-semibold text-brand-black/90 truncate">
            {products.length === 1
              ? products[0].name
              : products.map((p) => p.name).join(', ')}
          </p>
        </div>
        <button
          type="button"
          onClick={onClearProducts}
          className="w-8 h-8 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-black/[0.04] transition-colors shrink-0"
          aria-label="Cambiar selección"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {/* CTA para sumar / editar productos del outfit — refuerza que se
          pueden combinar varios. Con un solo producto invita a sumar; con
          más de uno (ya hay un outfit) pasa a "Editar outfit". Solo en
          plantilla / imagen-como-modelo. NO en el influencer (showGancho):
          ese modo genera con un solo producto (ADR-563). */}
      {onAddProducts && (kind === 'template' || isDesignModelo) && !showGancho && (
        <button
          type="button"
          onClick={onAddProducts}
          className="w-full inline-flex items-center justify-center gap-2 py-3 px-5 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/80 hover:text-brand-primary text-[14px] font-medium transition-colors"
        >
          {products.length > 1 ? (
            <>
              <Pencil className="w-4 h-4" />
              Editar outfit
            </>
          ) : (
            <>
              <Plus className="w-4 h-4" />
              Añadir producto al outfit
            </>
          )}
        </button>
      )}
    </div>
  );

  /** La card de Productos: lo mismo que el chip, con la forma de las cards del modo. */
  const cardProductos = onAbrirProductosLateral ? (
    <CardPicker
      etiqueta={products.length > 1 ? 'Productos' : 'Producto'}
      valor={
        products.length === 0
          ? 'Sin elegir'
          : products.length === 1
            ? products[0].name
            : `${products.length} productos`
      }
      imagenes={products.slice(0, 4).map((pr) => pr.imageUrl)}
      extra={products.length > 4 ? `+${products.length - 4}` : undefined}
      vacio={<Plus className="w-6 h-6 text-brand-black/25" />}
      onClick={onAbrirProductosLateral}
    />
  ) : null;

  /** La card de la PLANTILLA elegida — la primera de la columna, arriba de Producto. */
  const cardPlantilla =
    onAbrirPlantillaLateral && plantillaActual ? (
      <CardPicker
        etiqueta="Plantilla"
        valor={plantillaActual.titulo}
        imagenes={plantillaActual.imagen ? [plantillaActual.imagen] : []}
        vacio={<LayoutGrid className="w-6 h-6 text-brand-black/25" />}
        onClick={onAbrirPlantillaLateral}
        // Imagen/Video vive ACÁ, al final de la card y a la izquierda del chevron
        // (ADR-2026-09-08-el-switch-de-imagen-o-video-va-en-la-card-de-la-plantilla): es
        // una propiedad de la plantilla elegida —con qué se la va a generar—, no un
        // control suelto de la columna.
        accesorio={switchMedia}
      />
    ) : null;

  /** La card de Modelo. Misma etiqueta y misma miniatura que en el modo Modelo. */
  const cardModelo = onAbrirModeloLateral ? (
    <CardPicker
      etiqueta={palabra.titulo}
      valor={modelLabel}
      imagenes={(() => {
        // La `imageUrl` de la selección es una signed URL que caduca (la selección se
        // restaura de `localStorage`), así que manda la de `userModels` y ésa queda de
        // respaldo — el mismo criterio que `imagenDeModelo` en el modo Modelo.
        if (modelSelection?.mode === 'preset') {
          const m = userModels.find((u) => String(u.id) === modelSelection.id);
          const foto = m?.thumbUrl ?? m?.thumbnailUrl ?? modelSelection.imageUrl;
          return foto ? [foto] : [];
        }
        if (modelSelection?.mode === 'upload') return [modelSelection.previewUrl];
        return [];
      })()}
      vacio={<Sparkles className="w-6 h-6 text-brand-black/25" />}
      onClick={onAbrirModeloLateral}
    />
  ) : null;

  // ModelPickerInline (desktop) — usado en el flujo estándar y dentro de la
  // card "Modelo" del layout del modo influencer.
  const modelPickerInlineEl = (
    <ModelPickerInline
      selection={modelSelection}
      onChange={onModelChange}
      // Influencer: el modelo se elige sí o sí (sin tile "Automático").
      hideAuto={showGancho}
      view={modelView}
      onViewChange={handleModelViewChange}
      userModels={userModels}
      userId={userAuthId}
      companyId={userCompanyId}
      onModelCreated={onModelCreated}
      // Multi-mode para E-commerce poses (kind: 'design') con
      // más de 1 persona. Las plantillas tradicionales no usan
      // multi así que multiEnabled queda en false y el picker
      // sigue como single.
      selectionsExtra={modelSelectionsExtra}
      onChangeExtras={isDesign ? onModelChangeExtras : undefined}
      multiEnabled={isDesign && multiModelEnabled}
      multiProducts={products.map((p) => ({
        id: p.id,
        name: p.name,
        imageUrl: p.imageUrl,
      }))}
      multiProductsPerSlot={multiProductsPerSlot}
      onTapSlotProducts={onTapSlotProducts}
      lockSlotCount={lockSlotCount}
    />
  );

  // Campo de instrucción libre ("Instrucción adicional" / "Otra cosa…"). Es el
  // MISMO control en los cinco layouts del panel (mobile/desktop ×
  // plantilla/e-commerce/influencer), así que vive acá como helper: si no, el
  // markup —y ahora el botón de reusar instrucciones— habría que repetirlo
  // cinco veces y mantenerlo sincronizado a mano.
  const otraCosaField = (placeholder: string, wrapperClass = '') => (
    <div className={`relative ${wrapperClass}`}>
      <input
        type="text"
        value={otraConfig}
        onChange={(e) => onOtraChange(e.target.value)}
        placeholder={placeholder}
        // `pr-[104px]` reserva el lugar del botón, que va DENTRO del campo:
        // sin eso, el texto tipeado le pasa por debajo.
        className="w-full h-12 pl-4 pr-[104px] rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
      />
      {/* Se auto-oculta cuando la empresa todavía no usó ninguna instrucción. */}
      <PromptRecientesButton
        tipo="instruccion"
        onPick={onOtraChange}
        titulo="Instrucciones usadas antes"
        className="absolute right-2 top-1/2 -translate-y-1/2"
      />
    </div>
  );

  // En desktop (`stickyGenerate = true`) el panel es `flex flex-col flex-1`
  // y el botón Generar usa `mt-auto` para pegarse al bottom. En mobile el
  // contenido fluye naturalmente sin estirar el panel.
  return (
    <div
      className={stickyGenerate ? 'flex flex-col min-h-full shrink-0' : ''}
    >
      {/* Wrapper con padding-bottom = alto de la barra Generar sticky, para
          que el ÚLTIMO control (Instrucción adicional / Configurar video) se
          pueda scrollear por ENCIMA de la barra en vez de quedar tapado.
          Cubre TODO lo que va arriba del Generar (incluido "Configurar
          video", que antes quedaba fuera). Aplica siempre que el Generar es
          STICKY (desktop, o design-flow en mobile) — antes solo desktop, por
          eso en mobile el último input quedaba tapado. En contenido corto el
          `mt-auto` del Generar lo absorbe. */}
      <div
        className={
          stickyGenerate && !desktopShowOnlyPicker && !hideGenerateButton
            ? 'pb-24'
            : ''
        }
      >
      <div
        className={
          isDesktop
            ? // Con cards, las dos van JUNTAS —son la misma lista de decisiones— y el
              // aire grande queda para separarlas del título de arriba y de la
              // instrucción de abajo (ADR-2026-09-08-las-cards-del-paso-3-van-juntas).
              cardsLateral
              ? 'space-y-2 pt-4'
              : 'space-y-6'
            : 'space-y-5'
        }
      >
      {/* Products chip — escondido en desktop cuando el picker está en
          sub-vista (create/upload) para dejarle todo el panel a esa.
          En `design-modelo`/`design-auto` desktop también lo
          escondemos porque DesignModeloPanel lo renderiza como una
          card propia. */}
      {/* En el layout desktop del modo influencer (showGancho) el chip va
          adentro de la card "Producto" — no acá arriba. */}
      {cardsLateral && cardPlantilla}
      {!desktopShowOnlyPicker &&
        !(isDesignFlow && isDesktop) &&
        !isAnuncio &&
        // fotos-producto renderiza su propia card de Producto (en las dos
        // variantes), así que el chip de arriba sería un duplicado.
        !isFotosProducto &&
        !(showGancho && isDesktop) &&
        // Con panel lateral, la elección de productos se abre a la derecha y acá queda
        // la card (ADR-2026-09-08-la-configuracion-de-plantillas-se-elige-con-cards-y-panel-lateral).
        (cardsLateral ? cardProductos : productsChipBlock)}

      {/* Question — solo en mobile y NO en design-modelo/design-auto
          (esos tienen su propio layout de cards). En desktop el
          ModelPickerInline ocupa mucho espacio vertical y la pregunta
          queda redundante. En el modo "Video con influencer" (showGancho)
          tampoco va: el panel ya tiene su propia jerarquía (Gancho +
          opciones), la pregunta sobra. */}
      {!isDesktop &&
        !isDesignFlow &&
        !isAnuncio &&
        !isFotosProducto &&
        !showGancho && (
          <p className="text-[15.5px] font-semibold text-brand-black/90">
            ¿Querés realizarle algún cambio a la configuración?
          </p>
        )}

      {isFotosProducto ? (
        // ── Modo 'fotos-producto': producto + vistas + fondo reducido +
        // formato/instrucción. El fondo comparte el state `fondoSelection`
        // con los otros modos, así que lo estrechamos a las dos variantes
        // que este modo soporta (blanco / descripción) y caemos a blanco si
        // viniera con otra (ej. un restore de design-modelo).
        <FotosProductoPanel
          variant={variant}
          products={products}
          onClearProducts={onClearProducts}
          onRemoveProduct={onRemoveProduct}
          onAddProducts={onAddProducts}
          vistas={vistasProducto}
          vistaIds={vistaIds}
          onVistaIdsChange={onVistaIdsChange ?? (() => undefined)}
          vistaProductosExcluidos={vistaProductosExcluidos}
          onToggleVistaProducto={onToggleVistaProducto}
          totalImagenes={fotosProductoTotalImagenes}
          fondoSelection={
            esFondoProducto(fondoSelection)
              ? fondoSelection
              : { mode: 'blanco' }
          }
          onFondoChange={onFondoSelectionChange}
          formatoLabel={formatoLabel}
          onOpenFormat={onOpenFormatPicker}
          otraConfig={otraConfig}
          onOtraChange={onOtraChange}
          vistasGridId={vistasGridId}
        />
      ) : isAnuncio ? (
        // ── Modo 'anuncio': AnuncioPanel renderiza productos chip + 3
        // campos textuales + grilla de colores + Formato/Otra config.
        // Props con fallback defensivo (default state + no-op handlers)
        // por si el padre olvidó pasarlas — no rompe el render.
        <AnuncioPanel
          variant={variant}
          products={products}
          imagenBase={imagenBase}
          onClearProducts={onClearProducts}
          titulo={anuncioTitulo ?? { hidden: false, value: '', font: '' }}
          onTituloChange={onAnuncioTituloChange ?? (() => undefined)}
          descripcion={
            anuncioDescripcion ?? { hidden: false, value: '', font: '' }
          }
          onDescripcionChange={
            onAnuncioDescripcionChange ?? (() => undefined)
          }
          cta={anuncioCta ?? { hidden: false, value: '', font: '' }}
          onCtaChange={onAnuncioCtaChange ?? (() => undefined)}
          colors={anuncioColors ?? []}
          onColorsChange={onAnuncioColorsChange ?? (() => undefined)}
          brandColors={anuncioBrandColors ?? []}
          autoImageCount={autoImageCount}
          onAutoImageCountChange={onAutoImageCountChange}
          autoImageCountMax={autoImageCountMax}
          formatoLabel={formatoLabel}
          onOpenFormat={onOpenFormatPicker}
          otraConfig={otraConfig}
          onOtraChange={onOtraChange}
          minimalHeaders="mobile"
        />
      ) : isDesignFlow ? (
        // ── Modo 'design-modelo': layout unificado con TODAS las opciones
        // (Modelo + Pose + Fondo + Formato + Otra config). Desktop usa
        // pickers inline donde aplica (Modelo y Fondo) + carrusel pose
        // con "Ver más" → sheet. Mobile usa botones que abren sheets.
        <PanelDeModelo
          variant={variant}
          hideAutoPickers={isDesignAuto}
          soloModeloYPose={configureDosColumnas}
          autoImageCount={autoImageCount}
          onAutoImageCountChange={onAutoImageCountChange}
          autoImageCountMax={autoImageCountMax}
          products={products}
          onClearProducts={onClearProducts}
          onAddProducts={onAddProducts}
          modelSelection={modelSelection}
          onModelChange={onModelChange}
          modelSelectionsSerie={modelSelectionsSerie}
          onToggleModelSerie={onToggleModelSerie}
          modelSelectionsExtra={modelSelectionsExtra}
          onModelChangeExtras={onModelChangeExtras}
          multiModelEnabled={multiModelEnabled}
          onMultiModelEnabledChange={onMultiModelEnabledChange}
          onOpenModelPicker={onOpenModelPicker}
          onOpenModelSheet={onOpenModelSheet}
        onOpenModelSheetForMultiSlot={onOpenModelSheetForMultiSlot}
        multiProductsPerSlot={multiProductsPerSlot}
        onTapSlotProducts={onTapSlotProducts}
          onOpenFondoSheet={onOpenFondoSheet}
          userModels={userModels}
          userAuthId={userAuthId}
          userCompanyId={userCompanyId}
          onModelCreated={onModelCreated}
          modelView={modelView}
          onModelViewChange={handleModelViewChange}
          allPoses={allPoses}
          customPoses={customPoses}
          poseIds={poseIds}
          onPoseIdsChange={onPoseIdsChange}
          multiPoseEnabled={multiPoseEnabled}
          onOpenPosePicker={onOpenPosePicker}
          onSubirPose={onSubirPose}
          bibliotecaFondos={bibliotecaFondos}
          fondosGuardados={fondosGuardados}
          fondoSelection={fondoSelection}
          onFondoSelectionChange={onFondoSelectionChange}
          onOpenFondoPicker={onOpenFondoPicker}
          fondoView={fondoView}
          onFondoViewChange={onFondoViewChange}
          onFondoSaved={onFondoSaved}
          formatoLabel={formatoLabel}
          onOpenFormat={onOpenFormatPicker}
          resolucion={resolucion}
          onResolucionChange={onResolucionChange}
          otraConfig={otraConfig}
          onOtraChange={onOtraChange}
          lockSlotCount={lockSlotCount}
        />
      ) : isDesktop && panelView === 'format' ? (
        // ── Desktop format sub-view: ocupa el panel completo, mismo
        // patrón que el modelo create/upload. Tap en opción commitea y
        // vuelve al main.
        <FormatPickerInline
          selectedId={formatoVal}
          onPick={(id) => {
            onFormatoChange(id);
            setPanelView('main');
          }}
          onBack={() => setPanelView('main')}
        />
      ) : isDesktop && showGancho ? (
        // ── Desktop "Video con influencer": pantalla propia con cards
        // estilo design-modelo (título a la izquierda + contenido a la
        // derecha), sobre fondo transparente con scroll de ventana
        // (isFullScreenConfigure en PresetDetailSheet). ADR-533/534.
        desktopShowOnlyPicker ? (
          // Sub-vista del ModelPicker (create/upload) ocupa el panel entero.
          <>{!hideModelo && modelPickerInlineEl}</>
        ) : (
          <>
            {sectionCard(
              'Producto',
              'Vas a generar el video con estos productos.',
              productsChipBlock,
            )}
            {!hideModelo &&
              sectionCard(
                'Modelo',
                'La influencer de IA que muestra tu producto en el video.',
                // El id lo usa el pill flotante para scroll+shake cuando
                // falta elegir modelo (obligatorio en este modo).
                <div id={`${influencerIdPrefix}-modelo-card`}>
                  {modelPickerInlineEl}
                </div>,
              )}
            {sectionCard(
              'Planeamiento del video',
              'El gancho define la apertura del video: los primeros segundos que retienen al espectador.',
              // Carrusel horizontal de ganchos (como el picker de modelos).
              // El id lo usa el pill flotante para scroll+shake cuando falta
              // elegir gancho (ADR-552/553).
              <div id={`${influencerIdPrefix}-gancho-card`}>
                <GanchoSection
                  className=""
                  inlineCarousel
                  hideExplainer
                  selectedGanchoId={ganchoId}
                  onSelectGancho={onGanchoIdChange}
                />
              </div>,
              <button
                type="button"
                onClick={() => setGanchoExplainerOpen(true)}
                className="text-[12.5px] text-brand-primary hover:opacity-75 underline-offset-2 hover:underline transition-opacity"
              >
                ¿Qué es un gancho?
              </button>,
            )}
            <GanchoExplainerSheet
              open={ganchoExplainerOpen}
              onClose={() => setGanchoExplainerOpen(false)}
            />
            {voiceGuion && onVoiceGuionChange &&
              sectionCard(
                'Voz y guion',
                'Elegí la voz del video y decidí el guion: automático, escrito por vos o grabado con tu voz.',
                <VoiceGuionSection
                  className=""
                  companyId={userCompanyId}
                  value={voiceGuion}
                  onChange={onVoiceGuionChange}
                  videoDuration={videoDuration}
                  onVideoDurationChange={onVideoDurationChange}
                  sinGancho={isSinGancho(ganchoId)}
                  sinProducto={sinProducto}
                  dialogoLoading={dialogoLoading}
                  dialogoError={dialogoError}
                  domIdPrefix={influencerIdPrefix}
                />,
              )}
            {sectionCard(
              'Opciones adicionales',
              'Formato del video y ajustes finales.',
              <div className="flex flex-col gap-2.5">
                <button
                  type="button"
                  onClick={handleOpenFormat}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
                >
                  <span className="inline-flex items-center gap-2 text-[13.5px] text-[#3C3C43]/70">
                    <ImageIcon className="w-3.5 h-3.5" />
                    Formato
                  </span>
                  <span className="text-[13.5px] font-medium text-brand-black/85">
                    {formatoLabel}
                  </span>
                </button>
                {otraCosaField('Instrucción adicional')}
              </div>,
            )}
          </>
        )
      ) : isDesktop ? (
        // ── Desktop: ModelPickerInline arriba + Fondo/Formato/Otra abajo
        // Cuando el picker está en create/upload, las otras secciones se
        // esconden para que ese sub-flow ocupe todo el panel del costado.
        // Si la plantilla no acepta modelo (`hideModelo`), saltamos el
        // ModelPickerInline y dejamos solo Formato + Instrucción.
        <>
          {!hideModelo && (cardsLateral ? cardModelo : modelPickerInlineEl)}
          {/* Con cards y panel lateral, la columna queda como la del modo Modelo: arriba
              lo que se elige, en el medio la instrucción y abajo el pie con Formato y el
              Generar (ADR-2026-09-08-el-pie-del-paso-3-de-plantillas-es-el-del-modo-modelo).
              Sin el rótulo "Opciones adicionales": con una sola cosa arriba del pie, el
              título encabezaba una sección de un solo elemento. */}
          {!desktopShowOnlyPicker && cardsLateral && (
            // `pt-2` sobre los 8px del `space-y-2`: la instrucción no es una card más de
            // la lista, así que se despega de ellas.
            <div className="pt-2">
              <CampoInstruccion valor={otraConfig} onChange={onOtraChange} />
            </div>
          )}
          {!desktopShowOnlyPicker && !cardsLateral && (
              // Sección "Opciones adicionales" — paralelo visual a la
              // etiqueta de Modelo arriba.
              <div className="flex flex-col gap-2.5">
                <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
                  Opciones adicionales
                </p>
                {/* E-commerce (kind 'design') usa el mismo layout que las
                    plantillas tradicionales — Formato + Instrucción adicional. */}
                <button
                  type="button"
                  onClick={handleOpenFormat}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
                >
                  <span className="inline-flex items-center gap-2 text-[13.5px] text-[#3C3C43]/70">
                    <ImageIcon className="w-3.5 h-3.5" />
                    Formato
                  </span>
                  <span className="text-[13.5px] font-medium text-brand-black/85">
                    {formatoLabel}
                  </span>
                </button>
                {otraCosaField('Instrucción adicional')}
              </div>
            )}
        </>
      ) : isDesign ? (
        // ── Mobile + design (E-commerce): mismo layout que las
        // plantillas tradicionales — Modelo + Formato en 2 cols, con
        // "Otra cosa" abajo. NO mostramos picker de Pose porque la
        // plantilla EN SÍ ES la pose: el `poseIds` se auto-popula desde
        // el card que el user tappeó en la home (ver ecommercePoseMeta
        // effect). El fondo siempre es blanco para E-commerce.
        <>
          <div className="grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={onOpenModelPicker}
              className="flex flex-col items-start text-left px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors min-w-0"
            >
              <span className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
                <User className="w-3 h-3" />
                {palabra.titulo}
              </span>
              <span className="text-[14.5px] font-semibold text-brand-black/90 mt-0.5 truncate w-full">
                {modelLabel}
              </span>
            </button>
            <button
              type="button"
              onClick={handleOpenFormat}
              className="flex flex-col items-start text-left px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors min-w-0"
            >
              <span className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                Formato
              </span>
              <span className="text-[14.5px] font-semibold text-brand-black/90 mt-0.5 truncate w-full">
                {formatoLabel}
              </span>
            </button>
          </div>
          {otraCosaField('Otra cosa…')}
        </>
      ) : (
        // ── Mobile + template: Modelo (sheet) + Formato 2-col, Otra cosa.
        // Si la plantilla no acepta modelo (`hideModelo`), el Modelo
        // desaparece y Formato pasa a ocupar el full-width.
        <>
          {hideModelo ? (
            <button
              type="button"
              onClick={handleOpenFormat}
              className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
            >
              <span className="inline-flex items-center gap-2 text-[13.5px] text-[#3C3C43]/70">
                <ImageIcon className="w-3.5 h-3.5" />
                Formato
              </span>
              <span className="text-[13.5px] font-medium text-brand-black/85">
                {formatoLabel}
              </span>
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              {/* El id (solo influencer) lo usa el hint del Generar para
                  scroll+shake cuando falta elegir modelo. */}
              <button
                type="button"
                id={
                  showGancho ? `${influencerIdPrefix}-modelo-card` : undefined
                }
                onClick={onOpenModelPicker}
                className="flex flex-col items-start text-left px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors min-w-0"
              >
                <span className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {palabra.titulo}
                </span>
                <span className="text-[14.5px] font-semibold text-brand-black/90 mt-0.5 truncate w-full">
                  {modelLabel}
                </span>
              </button>
              <button
                type="button"
                onClick={handleOpenFormat}
                className="flex flex-col items-start text-left px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors min-w-0"
              >
                <span className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55 inline-flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" />
                  Formato
                </span>
                <span className="text-[14.5px] font-semibold text-brand-black/90 mt-0.5 truncate w-full">
                  {formatoLabel}
                </span>
              </button>
            </div>
          )}
          {/* "Otra cosa…" — en el modo "Video con influencer" (showGancho)
              este input se mueve al fondo de todo (después del Gancho) como
              "Instrucción adicional", así que acá NO va. */}
          {!showGancho && otraCosaField('Otra cosa…')}
        </>
      )}
      </div>

      {/* Configurar video — solo cuando el toggle del header está en
          'video' y el preset es 'template'. Va justo antes del Generar
          para que el user lo vea sin scrollear más. El parent abre el
          sheet de configuración (duración + escenas múltiples).
          En el modo "Video con influencer" este botón NO va (ni desktop
          ni mobile): la duración es una barra inline dentro de "Voz y
          guion", solo visible con guion Automático (ADR-528). */}
      {videoModeEnabled && onOpenVideoConfig && !showGancho && (
        <div className="mt-5">
          <button
            type="button"
            onClick={onOpenVideoConfig}
            className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 active:bg-neutral-50 transition-colors"
          >
            <span className="inline-flex items-center gap-2 text-[13.5px] text-[#3C3C43]/70">
              <Play className="w-3.5 h-3.5" />
              Configurar video
            </span>
            <span className="inline-flex items-center gap-2 text-[13.5px] font-medium text-brand-black/85">
              {videoDuration}s
              {videoMultiScene && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary text-[11px] font-semibold">
                  Multi
                </span>
              )}
              <ChevronRight className="w-3.5 h-3.5 text-[#3C3C43]/40" />
            </span>
          </button>
        </div>
      )}

      {/* Gancho (mobile) — opción adicional del modo "Video con influencer".
          En DESKTOP el gancho va en la card "Planeamiento del video", así que
          acá solo se renderiza en mobile. VIVE DENTRO del wrapper pb-24: si
          quedara afuera, el padding del wrapper se convierte en un hueco
          entre el contenido y estas secciones (visto en mobile, ADR-534). */}
      {showGancho && !isDesktop && (
        <div id={`${influencerIdPrefix}-gancho-card`}>
          <GanchoSection
            selectedGanchoId={ganchoId}
            onSelectGancho={onGanchoIdChange}
          />
        </div>
      )}

      {/* Voz y guion (mobile influencer) — debajo del Gancho, antes de la
          Instrucción adicional. En desktop va como card propia. ADR-525. */}
      {showGancho && !isDesktop && voiceGuion && onVoiceGuionChange && (
        <VoiceGuionSection
          companyId={userCompanyId}
          value={voiceGuion}
          onChange={onVoiceGuionChange}
          videoDuration={videoDuration}
          onVideoDurationChange={onVideoDurationChange}
          sinGancho={isSinGancho(ganchoId)}
          sinProducto={sinProducto}
          dialogoLoading={dialogoLoading}
          dialogoError={dialogoError}
          domIdPrefix={influencerIdPrefix}
        />
      )}

      {/* Instrucción adicional (mobile influencer) — última opción, abajo
          del todo: después del Gancho y justo antes del Generar. */}
      {showGancho &&
        !isDesktop &&
        otraCosaField('Instrucción adicional', 'mt-5')}
      </div>
      {/* /wrapper con padding-bottom para librar la barra Generar sticky */}

      {/* Generate button — cuando `stickyGenerate` queda pinned al
          bottom del scroll container con `position: sticky`. Los
          negative margins (`-mx-5 md:-mx-6 -mb-6`) cancelan el padding
          del wrapper padre así el bg-white llega a los bordes y al pie
          del aside; el `pt-3 pb-4 md:pb-6` re-establece el espaciado
          interno del botón. `mt-auto` mantiene el push-to-bottom para
          contenido corto (sin él, sticky arranca en la posición natural
          del flex flow — que con content chico queda flotando al medio).
          Escondido en desktop cuando el picker está en create/upload
          — esa sub-vista tiene su propio botón "Listo" o "Subir".
          También se esconde cuando `hideGenerateButton`. */}
      {!desktopShowOnlyPicker && !hideGenerateButton && (
        <div
          className={
            stickyGenerate
              ? // Con cards + panel lateral el pie es el del modo Modelo: la misma barra,
                // pero sin los 24px de aire debajo del botón —ahí no va nada más y se
                // leían como una franja blanca al pie de la columna—.
                `mt-auto sticky bottom-0 z-10 -mx-5 md:-mx-6 px-5 md:px-6 pt-3 bg-white/95 backdrop-blur-sm border-t border-black/[0.05] ${
                  // Sin `-mb-6` ni los 24px de abajo: con cards, el scroller de afuera
                  // tampoco lleva `pb-6`, así que el pie apoya en el borde del panel. Con
                  // el margen negativo quedaba tirando 24px hacia afuera y con el padding
                  // del scroller, el `bottom: 0` del sticky se pegaba 24px más arriba —
                  // la franja blanca que se veía debajo del botón.
                  cardsLateral ? 'pb-4' : '-mb-6 pb-4 md:pb-6'
                }`
              : 'mt-5'
          }
        >
          {/* Calidad · Formato, en el PIE y no arriba: son ajustes de la salida, y en esta
              columna lo que va arriba es lo que se elige (producto, modelo). Son las mismas
              celdas y el mismo orden del modo Modelo. La Calidad entra sólo en las
              plantillas de imagen (`calidadEnElPie`,
              ADR-2026-09-28-las-plantillas-eligen-la-calidad): en las de video no cambia
              nada de lo que se genera, y un control que no cambia nada es un control muerto. */}
          {cardsLateral && (
            // `grid grid-cols-2` + `solo`: la celda está pensada para una fila de dos
            // (Calidad · Formato) y sin la grilla se encoge al ancho de su texto. Sola,
            // ocupa la línea entera, que es como se ve el pie con un solo ajuste.
            // `abrePanelLateral` la marca como disparador: sin eso, el click-afuera del
            // panel lo cerraría en el mismo gesto que lo abre.
            <div className="mb-3 grid grid-cols-2 gap-2">
              {calidadEnElPie && calidad && onCalidadChange && (
                <CeldaAjuste etiqueta="Calidad">
                  <CalidadSegmentada calidad={calidad} onChange={onCalidadChange} />
                </CeldaAjuste>
              )}
              <CeldaAjuste
                etiqueta="Formato"
                onClick={handleOpenFormat}
                solo={!(calidadEnElPie && calidad && onCalidadChange)}
                abrePanelLateral
              >
                <ValorConChevron texto={formatoLabel} />
              </CeldaAjuste>
            </div>
          )}
          {/* Sin cards (mobile): Formato vive arriba, junto al modelo, así que al pie baja
              sólo la Calidad, a lo ancho — el mismo lugar donde Automático mobile la pone,
              pegada a lo que cuesta la tanda. */}
          {!cardsLateral && calidadEnElPie && calidad && onCalidadChange && (
            <div className="mb-3 grid grid-cols-2 gap-2">
              <CeldaAjuste etiqueta="Calidad" solo>
                <CalidadSegmentada calidad={calidad} onChange={onCalidadChange} />
              </CeldaAjuste>
            </div>
          )}
          {/* Qué es una producción — ADR-2026-08-20-la-produccion-se-anuncia-en-el-panel.
              Va debajo del stepper y arriba del Generar, o sea entre la causa (el número) y
              la consecuencia (el botón que cambió de nombre).

              El número sale del MISMO `imageCount` que el stepper, no de una constante: si
              el texto dijera un fijo, subir el contador lo dejaría mintiendo.

              Dice "imágenes" y no "fotos" —la palabra del mockup— para no contradecir al
              stepper de dos líneas más arriba, que cuenta en "imagen/imágenes". Si el copy
              tiene que decir "fotos", va en los dos lugares.

              El color es `text-[#3C3C43]/45`, el MISMO tono del otro hint de este panel
              (`showPosesCountHint`, unas líneas abajo) y no un `text-brand-black/NN`: los
              tokens `brand-*` no declaran `<alpha-value>`, así que con modificador de
              opacidad Tailwind no emite NADA y el texto se quedaría con el color heredado.
              Verificado contra `dist/assets/*.css`: `.text-\[\#3C3C43\]\/45` está;
              `.text-brand-black\/80` —que este archivo usa en varios lugares— no.

              El ícono no lleva color propio: hereda por `currentColor` del `<p>`. */}
          {isProductionMode && (
            <p className="mb-3 -mt-1 flex items-start gap-1.5 px-1 text-[12.5px] leading-snug text-[#3C3C43]/45">
              <LayoutGrid className="mt-[2px] w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              <span>
                Producción completa: {imageCount} imágenes con distintos planos y
                poses, mismo estilo y locación.
              </span>
            </p>
          )}
          {/* Cantidad de imágenes del modo Imagen personalizada (mobile) —
              en DESKTOP este contador vive en el header, a la izquierda del
              pill Generar (PresetDetailSheet). Mismo markup que el stepper
              de plantillas para no inventar un tercer estilo. Solo con una
              pose (o pose Automática) y sin varios modelos: ver
              designModeloCount.ts / ADR-2026-07-30-imagen-personalizada-cantidad. */}
          {showDesignModeloCount && (
            <div className="mb-3 flex items-center justify-between gap-3 px-4 py-2.5 rounded-2xl bg-white border border-neutral-200">
              <span className="text-[14.5px] font-medium text-brand-black/80">
                Cantidad
              </span>
              <div className="inline-flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() =>
                    onAutoImageCountChange(Math.max(1, designModeloCount - 1))
                  }
                  disabled={designModeloCount <= 1}
                  aria-label="Generar una imagen menos"
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-neutral-200 text-brand-black/70 hover:border-brand-primary/40 hover:text-brand-primary disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="min-w-[88px] text-center text-[14.5px] font-semibold text-brand-black/90 tabular-nums">
                  {designModeloCount}{' '}
                  {designModeloCount === 1 ? 'imagen' : 'imágenes'}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    onAutoImageCountChange(
                      Math.min(autoImageCountMax, designModeloCount + 1),
                    )
                  }
                  disabled={designModeloCount >= autoImageCountMax}
                  aria-label="Generar una imagen más"
                  className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-neutral-200 text-brand-black/70 hover:border-brand-primary/40 hover:text-brand-primary disabled:opacity-40 disabled:pointer-events-none transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          {/* ── Calidad + Formato, DENTRO del pie fijo ──────────────────────
              Viajaron acá desde el cuerpo del panel
              (ADR-2026-09-02-los-ajustes-de-automatico-viajan-al-pie-fijo): son los
              ajustes de la tanda y se leen junto con lo que la tanda va a costar, sin
              depender de dónde quedó el scroll. Arriba del resumen y del Generar, que
              es el orden de desktop.

              Mismas celdas que el pie de desktop (`AjustesRapidosProducto`), así que
              los cuatro controles del modo —instrucción aparte— se ven igual en las dos
              pantallas. */}
          {autoCountJuntoAlGenerar && (
            <div className="mb-2.5 grid grid-cols-2 gap-2">
              {calidad && onCalidadChange && (
                <CeldaAjuste etiqueta="Calidad">
                  <CalidadSegmentada
                    calidad={calidad}
                    onChange={onCalidadChange}
                  />
                </CeldaAjuste>
              )}
              <CeldaAjuste etiqueta="Formato" onClick={onOpenFormatPicker}>
                <ValorConChevron texto={formatoLabel} />
              </CeldaAjuste>
            </div>
          )}
          {/* Resumen de la tanda: cuántas imágenes y de qué combinación salen.
              Arriba del Generar y en tono bajo — es una aclaración, no una
              acción. */}
          {resumenTanda && (
            <p className="mb-2 text-center text-[12.5px] leading-snug text-[#3C3C43]/55">
              {resumenTanda}
            </p>
          )}
          {/* Wrapper con la card del hint DETRÁS del botón (mismo lenguaje
              que el pill flotante desktop, ADR-554): la card se alinea
              exacto a los bordes del botón y el mensaje emerge por ARRIBA
              con fade + colapso de altura. Para los modos sin hint queda
              siempre invisible. */}
          <div className="relative">
            <div
              aria-hidden
              className={`absolute inset-0 rounded-2xl bg-white/85 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] transition-opacity duration-300 ${
                generatePendiente ? 'opacity-100' : 'opacity-0'
              }`}
            />
            <div
              className={`relative z-10 overflow-hidden transition-all duration-300 ease-out ${
                generatePendiente ? 'max-h-16 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="pt-2 pb-1.5 px-4 text-center text-[12.5px] leading-snug text-[#3C3C43]/70">
                {generatePendiente
                  ? generateHintMsg
                  : lastInfluencerHintRef.current}
              </p>
            </div>
            <div className="relative z-10 flex items-stretch gap-2">
          {/* `[&>span]:mt-0`: `ContadorImagenes` trae un `mt-1` propio —viene de cuando
              vivía debajo de la etiqueta de una celda— y acá, en una caja centrada, esos
              4px lo descuelgan del botón de al lado. Se neutraliza desde afuera para no
              tocar el componente, que en sus otros usos sí necesita ese aire. */}
          {autoCountJuntoAlGenerar && (
            <div className="shrink-0 rounded-2xl bg-black/[0.035] px-2 flex items-center [&>span]:mt-0">
              <ContadorImagenes
                valor={autoCountEfectivo}
                max={autoImageCountMax}
                onChange={onAutoImageCountChange}
                disabled={products.length === 0}
              />
            </div>
          )}
          {contadorPlantillaJuntoAlGenerar && (
            <div className="shrink-0 rounded-2xl bg-black/[0.035] px-2 flex items-center [&>span]:mt-0">
              <ContadorImagenes
                valor={imageCount}
                max={IMAGE_COUNT_MAX}
                onChange={setImageCount}
              />
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              // Bloqueado: en vez de un botón muerto, scrolleamos hasta el
              // componente faltante y lo sacudimos (ADR-552/553).
              if (generatePendiente) {
                scrollToAndShake(generateMissingTargetId);
                return;
              }
              handleGenerateClick();
            }}
            aria-disabled={generatePendiente}
            className={`inline-flex items-center justify-center gap-2 py-3.5 px-4 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors whitespace-nowrap ${
              autoCountJuntoAlGenerar || contadorPlantillaJuntoAlGenerar
                ? 'flex-1 min-w-0'
                : 'w-full'
            } ${generatePendiente ? 'opacity-60' : ''}`}
          >
            {/* Con el contador al lado el botón NO lleva ícono, igual que el pie de
                Modelo y Automática (`PieConfiguracionModo`): entre el stepper, la
                estrella, "Generar producción" y la pastilla de créditos, el rótulo se
                partía en dos líneas — ADR-2026-09-08-plantillas-toma-el-contador-de-los-modos. */}
            {contadorPlantillaJuntoAlGenerar ? null : videoModeEnabled ? (
              <Play className="w-5 h-5" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            {/* "Generar producción" cuando el stepper pide más de una imagen —
                ADR-2026-08-20-la-produccion-se-anuncia-en-el-panel. El botón ya hacía otra
                cosa en ese caso (`handleGenerateClick` se va por `onGenerateProduction`);
                lo que faltaba era decirlo. El nombre vuelve solo a "Generar" al bajar el
                contador a 1, porque `isProductionMode` es el MISMO booleano que decide la
                rama — no hay forma de que el rótulo y la acción se separen. */}
            <span>
              {videoModeEnabled
                ? 'Generar video'
                : isProductionMode
                  ? 'Generar producción'
                  : 'Generar'}
            </span>
            {activeCreditCost != null && activeCreditCost > 0 && (
              <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
                <img
                  src="/images/token_icon.png"
                  alt=""
                  aria-hidden="true"
                  className="w-3.5 h-3.5 object-contain"
                />
                {activeCreditCost}
              </span>
            )}
          </button>
            </div>
          </div>
          {/* /wrapper hint+botón */}
        </div>
      )}
    </div>
  );
};
